<template>
<section class="map-radar-chart-map">
  <section class="color-level-bar-wrapper">
    <span class="data-unit">{{displayUnit}}</span>
    <canvas
      ref="colorLevelBarCanvas"
      :width="colorBarSectionWidth + 40"
      :height="(type === 'piecewise' ? (colorSectionHeight * (colorSectionLen + 2)) : 384)"
    />
  </section>

  <canvas ref="radarChartCanvas" class="radar-chart-canvas" />
</section>
</template>

<script>
import BaseMapTool from '../BaseMapTool'

import eventKeyMap from './eventKeyMap'

const COLOR_STEP = 60

const STROKE_STYLE = {
  strokeColor: '#FFF',
  strokeOpacity: 1
}

export default {
  name: 'MapToolWindSpeedRose',
  extends: BaseMapTool,
  props: {
    type: String,
    colorList: Array,
    colorBarValInterval: {
      type: Number,
      default: 4
    },
    minVal: Number,
    maxVal: Number,
    unit: String
  },
  data () {
    return {
      displayUnit: ''
    }
  },
  computed: {
    eventKeyMap: () => eventKeyMap,
    colorBarSectionWidth: () => 35,
    colorSectionHeight: () => 12,
    angleInterval: () => 12,
    axisInterval: () => 6,
    zIndex: () => 50,
    reverseOpacityColorList () {
      let reversColorList = [...this.colorList].reverse()
      return reversColorList.map(color => {
        let colorNum = parseInt(color.replace('#', ''), 16)
        return `rgba(${colorNum >> 16}, ${(colorNum & 0xFF00) >> 8}, ${colorNum & 0xFF}, 0.6)`
      })
    },
    colorSectionLen () {
      return this.colorList.length
    },
    valDiff () {
      return this.maxVal - this.minVal
    }
  },
  methods: {
    drawColorLevel (data) {
      let canvas = this.$refs.colorLevelBarCanvas
      let {colorBarSectionWidth, colorSectionHeight, colorList, colorBarValInterval} = this
      let {width, height} = canvas
      let ctx = canvas.getContext('2d')

      ctx.clearRect(0, 0, width, height)

      if (!data) {
        return
      }

      ctx.shadowBlur = 4
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'

      let colorLen = colorList.length
      let axisStep = 0
      let lg, colorStep, fillHeight

      switch (this.type) {
        case 'windArrow':
          lg = ctx.createLinearGradient(0, 0, 0, height)
          colorStep = 1 / (colorLen - 1)

          colorList.forEach((color, index) => {
            lg.addColorStop(index * colorStep, color)
          })

          fillHeight = height - (colorSectionHeight << 1)
          ctx.fillStyle = lg
          ctx.fillRect(0, colorSectionHeight, colorBarSectionWidth, fillHeight)
          axisStep = fillHeight / colorBarValInterval
          break
        case 'continuous':
          lg = ctx.createLinearGradient(0, 0, 0, height)
          colorStep = COLOR_STEP
          let colorInterval = (colorLen - 1) / colorStep

          for (let i = 0; i <= colorInterval; i++) {
            lg.addColorStop(i / colorInterval, colorList[i * colorStep])
          }

          fillHeight = height - (colorSectionHeight << 1)
          ctx.fillStyle = lg
          ctx.fillRect(0, colorSectionHeight, colorBarSectionWidth, fillHeight)
          axisStep = fillHeight / colorBarValInterval
          break
        default:
          colorList.forEach((color, index) => {
            let y = (index + 1) * colorSectionHeight
            ctx.fillStyle = color
            ctx.fillRect(0, y, colorBarSectionWidth, colorSectionHeight)
          })

          axisStep = colorLen * colorSectionHeight / colorBarValInterval
          break
      }

      let valStep = this.valDiff / colorBarValInterval
      let fontSize = 14
      let textOffsetY = 5 // fontSize >> 1
      let textX = colorBarSectionWidth + 5

      ctx.font = `${fontSize}px microsoft yahei`
      ctx.fillStyle = 'RGB(0, 0, 0)'
      ctx.shadowColor = 'rgba(255, 255, 255, 0.5)'

      for (let i = 0; i <= colorBarValInterval; i++) {
        let y = i * axisStep + colorSectionHeight + textOffsetY
        let textVal = (this.maxVal - i * valStep).toString()
        ctx.fillStyle = '#000'
        ctx.fillText(textVal, textX + 1, y + 1)
        ctx.fillStyle = 'rgba(255,255,255,.9)'
        ctx.fillText(textVal, textX, y)
      }

      this.displayUnit = this.unit
    },
    drawWindArrowChart (map, data) {
      let {mapLngLat} = this

      if (!mapLngLat) {
        return
      }

      const COLOR_CANVAS_WIDTH = 20
      const COLOR_CANVAS_HEIGHT = 256
      let colorList = this.colorList
      let tmpColorCanvas = document.createElement('canvas')
      tmpColorCanvas.width = COLOR_CANVAS_WIDTH
      tmpColorCanvas.height = COLOR_CANVAS_HEIGHT
      let tmpColorCtx = tmpColorCanvas.getContext('2d')
      let lg = tmpColorCtx.createLinearGradient(0, 0, 0, COLOR_CANVAS_HEIGHT)
      let colorStep = 1 / (colorList.length - 1)
      let reverseColorList = [...colorList]
      reverseColorList.reverse()
      reverseColorList.forEach((color, index) => {
        lg.addColorStop(index * colorStep, color)
      })
      tmpColorCtx.fillStyle = lg
      tmpColorCtx.fillRect(0, 0, COLOR_CANVAS_WIDTH, COLOR_CANVAS_HEIGHT)
      let colorImageData = tmpColorCtx.getImageData(0, 0, 1, COLOR_CANVAS_HEIGHT).data

      let lngLatToContainer = map.lngLatToContainer
      let oLngLat = data.lngLat
      let oPX = lngLatToContainer(oLngLat)
      let currentPX = lngLatToContainer(mapLngLat)
      let offsetX = oPX.x - currentPX.x
      let offsetY = oPX.y - currentPX.y

      let radarChartCanvas = this.$refs.radarChartCanvas
      let radarChartWidth = radarChartCanvas.width
      let radarChartHeight = radarChartCanvas.height
      let tmpCanvas = document.createElement('canvas')
      tmpCanvas.width = radarChartWidth
      tmpCanvas.height = radarChartHeight
      let tmpCtx = tmpCanvas.getContext('2d')

      let dataList = data.dataList

      if (dataList instanceof Array) {
        let itemOffset = lngLatToContainer(mapLngLat.offset(60, 0)).x - currentPX.x
        let size = Math.round(itemOffset * 4)
        // let size = Math.round(itemOffset * 2)
        let halfSize = size / 2
        let bottomOffset = size * 2 / 3
        let {minVal, valDiff} = this
        let baseAngle = Math.PI / 180
        let SIN = Math.sin
        let COS = Math.cos
        let baseHalfDeg = 15
        let arrowD = size / SIN((90 - baseHalfDeg) * baseAngle)
        dataList.forEach(data => {
          let px = lngLatToContainer([data.longitude, data.latitude])
          let deg = data.deg + 180
          let angle = deg * baseAngle
          let x = px.x - offsetX
          let y = px.y - offsetY
          let sin = SIN(angle)
          let cos = COS(angle)
          let arrowOffsetX = halfSize * sin
          let arrowOffsetY = halfSize * cos
          let colorRatio = Math.max(Math.min((data.value - minVal) / valDiff, 1), 0)
          let colorIndex = Math.round(colorRatio * 255) * 4
          let [r, g, b] = colorImageData.slice(colorIndex, colorIndex + 3)
          let beginX = x + arrowOffsetX
          let endY = y - arrowOffsetY
          tmpCtx.fillStyle = `rgba(${r}, ${g}, ${b}, .9)`

          tmpCtx.beginPath()
          tmpCtx.moveTo(beginX, endY)
          tmpCtx.lineTo(beginX + arrowD * SIN((deg - (180 - baseHalfDeg)) * baseAngle), endY - arrowD * COS((deg - 165) * baseAngle))
          tmpCtx.lineTo(beginX + bottomOffset * SIN((deg + 180) * baseAngle), endY - bottomOffset * COS((deg + 180) * baseAngle))
          tmpCtx.lineTo(beginX + arrowD * SIN((deg + (180 - baseHalfDeg)) * baseAngle), endY - arrowD * COS((deg + 165) * baseAngle))
          tmpCtx.moveTo(beginX, endY)
          tmpCtx.fill()
          tmpCtx.closePath()
        })
      }

      let radarChartCtx = radarChartCanvas.getContext('2d')
      radarChartCtx.clearRect(0, 0, radarChartWidth, radarChartHeight)
      radarChartCtx.drawImage(
        tmpCanvas, // 规定要使用的图像、画布或视频。
        0, 0, // 开始剪切的 x 坐标位置。
        radarChartWidth, radarChartHeight, // 被剪切图像的高度。
        0, 0, // 在画布上放置图像的 x 、y坐标位置。
        radarChartWidth, radarChartHeight // 要使用的图像的宽度、高度
      )
    },
    drawRadarChart (map, data) {
      if (this.type === 'windArrow') {
        return this.drawWindArrowChart(map, data)
      }

      let radarChartCanvas = this.$refs.radarChartCanvas
      let radarChartWidth = radarChartCanvas.width
      let radarChartHeight = radarChartCanvas.height
      let radarChartCtx = radarChartCanvas.getContext('2d')

      radarChartCtx.clearRect(0, 0, radarChartWidth, radarChartHeight)

      let dataList = data.dataList
      let n = data.n
      let PI = Math.PI
      let baseAngle = PI / 180
      let {
        pxR: r,
        realR,
        scaleRatio,
        rangeStep,
        angleInterval,
        axisInterval,
        mapLngLat,
        minVal,
        valDiff,
        reverseOpacityColorList
      } = this
      // let valueList = dataList.map((data) => data.value)
      // let minVal = Math.min(...valueList)
      // let maxVal = Math.max(...valueList)
      // let valDiff = maxVal - minVal
      // let circleCenterLngLat = map.lngLat(...data.lngLat)
      let targetPixel = map.lngLatToContainer(mapLngLat)
      let centerX = radarChartWidth / 2
      let centerY = radarChartHeight / 2
      let tmpCanvas = document.createElement('canvas')

      centerX += targetPixel.x - centerX
      centerY += targetPixel.y - centerY

      tmpCanvas.width = radarChartWidth
      tmpCanvas.height = radarChartHeight

      let tmpCtx = tmpCanvas.getContext('2d')
      let rStep = r / axisInterval

      tmpCtx.lineWidth = 0.6
      tmpCtx.strokeStyle = '#FFF'

      for (let i = 0; i < axisInterval; i++) {
        tmpCtx.arc(centerX, centerY, r - rStep * i, 0, PI * 2, false)
      }

      let degStep = 360 / angleInterval
      let halfAngleInterval = angleInterval >> 1

      for (let i = 0; i < halfAngleInterval; i++) {
        let currentAngle = i * degStep * baseAngle
        let offsetX = r * Math.sin(currentAngle)
        let offsetY = r * Math.cos(currentAngle)

        tmpCtx.moveTo(centerX + offsetX, centerY - offsetY)
        tmpCtx.lineTo(centerX - offsetX, centerY + offsetY)
      }

      tmpCtx.stroke()

      // let lastDeg = dataList[0].azimuth - 0.001
      let perDegStep = 2.8
      let lastDeg = Math.ceil(dataList[0].azimuth - (perDegStep / 2))
      let colorLen = reverseOpacityColorList.length
      let degDataMap = {}
      let degDataList = dataList.reduce((lists, data) => {
        let deg = data.azimuth
        let list = degDataMap[deg]

        if (!list) {
          list = degDataMap[deg] = []
          lists.push([deg, list])
        }

        list.push(data)
        return lists
      }, [])

      degDataMap = null

      degDataList.forEach(([deg, list]) => {
        let currentDeg = parseFloat(deg)

        if ((currentDeg - lastDeg) > perDegStep) {
          lastDeg = Math.round(currentDeg - perDegStep)
        }

        let sin1 = Math.sin(lastDeg * baseAngle)
        let cos1 = Math.cos(lastDeg * baseAngle)
        let sin2 = Math.sin(currentDeg * baseAngle)
        let cos2 = Math.cos(currentDeg * baseAngle)

        list.forEach(data => {
          let distance = n * data.range * scaleRatio
          let distance2 = distance - rangeStep
          let beginX = centerX + distance * sin1
          let beginY = centerY - distance * cos1

          tmpCtx.moveTo(beginX, beginY)
          tmpCtx.beginPath()
          tmpCtx.lineTo(centerX + distance * sin2, centerY - distance * cos2)
          tmpCtx.lineTo(centerX + distance2 * sin2, centerY - distance2 * cos2)
          tmpCtx.lineTo(centerX + distance2 * sin1, centerY - distance2 * cos1)
          tmpCtx.lineTo(beginX, beginY)
          tmpCtx.closePath()

          let colorRatio = Math.min(Math.abs((data.value - minVal) / valDiff), 1)
          tmpCtx.fillStyle = reverseOpacityColorList[Math.floor(colorRatio * (colorLen - 1))]

          tmpCtx.fill()
        })

        lastDeg = currentDeg
      })

      let realRStep = realR / axisInterval
      let fontSize = 14
      let minTextInterval = 60
      let textTotalWidth = minTextInterval * axisInterval
      let times = r / textTotalWidth
      let xIndexStep = times >= 1.6 ? 1 : (times >= 0.8 ? 2 : (times >= 0.4 ? 3 : axisInterval))
      let yIndexStep = times < 0.2 ? axisInterval : (times < 0.8 ? 2 : 1)

      // tmpCtx.fillStyle = 'rgba(255,255,255,.8)'
      tmpCtx.font = `${fontSize}px microsoft yahei`

      for (let i = 0; i < axisInterval; i++) {
        let realRange = Math.round(realR - realRStep * i)
        let currentRPX = r - rStep * i
        let rangeText = realRange.toFixed(0)

        if ((i % yIndexStep) === 0) {
          let textCenterX = centerX - 20
          tmpCtx.fillStyle = 'rgba(0,0,0,.7)'
          tmpCtx.fillText(`${rangeText}m`, textCenterX + 1, centerY - currentRPX + 5 + 1)
          tmpCtx.fillText(`${rangeText}m`, textCenterX + 1, centerY + currentRPX + 5 + 1)
          tmpCtx.fillStyle = 'rgba(255,255,255,.8)'
          tmpCtx.fillText(`${rangeText}m`, textCenterX, centerY - currentRPX + 5)
          tmpCtx.fillText(`${rangeText}m`, textCenterX, centerY + currentRPX + 5)
        }

        if ((i % xIndexStep) === 0) {
          let textCenterY = centerY + 5
          tmpCtx.fillStyle = 'rgba(0,0,0,.7)'
          tmpCtx.fillText(`${rangeText}m`, centerX - currentRPX - 20 + 1, textCenterY + 1)
          tmpCtx.fillText(`${rangeText}m`, centerX + currentRPX - 20 + 1, textCenterY + 1)
          tmpCtx.fillStyle = 'rgba(255,255,255,.8)'
          tmpCtx.fillText(`${rangeText}m`, centerX - currentRPX - 20, textCenterY)
          tmpCtx.fillText(`${rangeText}m`, centerX + currentRPX - 20, textCenterY)
        }

        // if (i === 0) {
        //   tmpCtx.fillStyle = 'RGBA(255, 255, 255, 0.4)'
        //   tmpCtx.fill()
        // }
      }

      radarChartCtx.drawImage(
        tmpCanvas, // 规定要使用的图像、画布或视频。
        0, 0, // 开始剪切的 x 坐标位置。
        radarChartWidth, radarChartHeight, // 被剪切图像的高度。
        0, 0, // 在画布上放置图像的 x 、y坐标位置。
        radarChartWidth, radarChartHeight // 要使用的图像的宽度、高度
      )
    },
    setParams (map, data) {
      if (this.type === 'windArrow') {
        let lngLatFn = map.lngLat
        let [centerLng, centerLat] = data.lngLat = data.lngLat.map(n => parseFloat(n))
        let dataList = data.dataList
        let centerPoint = lngLatFn(centerLng, centerLat)
        let distances = dataList.map(data => centerPoint.distance([data.longitude, data.latitude]))
        this.realR = Math.ceil(Math.max(...distances))
      } else {
        let circleCenter = data.lngLat
        let circleCenterLngLat = map.lngLat(...circleCenter)
        let n = data.n
        let r = this.realR = Math.ceil(data.range * n)
        let point = circleCenterLngLat.offset(r, 0)
        let distancePX = this.pxR = Math.abs(map.lngLatToPixel(circleCenterLngLat).x - map.lngLatToPixel(point).x)
        let scaleRatio = this.scaleRatio = distancePX / r
        this.rangeStep = data.rangeStep * n * scaleRatio
      }
    },
    onDataChange (map, data) {
      console.time('onDataChange')
      this.setParams(map, data)
      // this.drawColorLevel(data)
      this.drawRadarChart(map, data)
      console.timeEnd('onDataChange')
    },
    onLngLatChange (map, data) {
      console.time('onLngLatChange')
      this.setParams(map, data)
      let circle = map.drawCircle({
        center: this.mapLngLat = map.lngLat(...data.lngLat),
        radius: this.realR, // 半径
        ...STROKE_STYLE,
        strokeWeight: 2,
        strokeOpacity: 0,
        fillOpacity: 0,
        fillColor: '#FFF'
      })

      map.handleSetFitView([circle])
      map.handleRemoveOverlay(circle)

      this.drawColorLevel(data)
      this.drawRadarChart(map, data)
      console.timeEnd('onLngLatChange')
    },
    onMapViewChange () {
      console.time('onMapViewChange')
      let {map, data} = this

      if (data) {
        this.setParams(map, data)
        // this.drawColorLevel(this.data)
        this.drawRadarChart(map, data)
      }

      console.timeEnd('onMapViewChange')
    },
    handleWindowResize () {
      let radarCanvas = this.$refs.radarChartCanvas
      let wrapper = this.$el
      let data = this.data

      radarCanvas.width = wrapper.offsetWidth
      radarCanvas.height = wrapper.offsetHeight

      if (data) {
        this.lngLatChange(data)
      }
    },
    clearMapZoomTimer () {
      clearTimeout(this.mapZoomTimer)
    },
    handleMapZoomChanged () {
      this.clearMapZoomTimer()

      this.mapZoomTimer = setTimeout(() => {
        this.onMapViewChange()
      }, 200)
    },
    handleMapMoving () {
      this.onMapViewChange()
    },
    clearChart (refEle) {
      if (refEle) {
        let ctx = refEle.getContext('2d')
        ctx.clearRect(0, 0, refEle.width, refEle.height)
      }
    },
    onClear () {
      let {colorLevelBarCanvas, radarChartCanvas} = this.$refs
      this.clearChart(colorLevelBarCanvas)
      this.clearChart(radarChartCanvas)
      this.pxR = this.scaleRatio = this.rangeStep = this.data = null
    }
  },
  created () {
    let eventBus = this.$eventBus
    eventBus.on('WINDOW_RESIZE', this.handleWindowResize)
  },
  mounted () {
    this.$nextTick(() => {
      this.handleWindowResize()
    })
  },
  beforeDestroy () {
    let eventBus = this.$eventBus
    eventBus.off('WINDOW_RESIZE', this.handleWindowResize)
    this.clearMapZoomTimer()
  }
}
</script>

<style lang="scss" scoped>
.map-radar-chart-map {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  .color-level-bar-wrapper {
    position: absolute;
    right: 0;
    bottom: 0;
    padding-top: 14px;
    /*pointer-events: none;*/

    .data-unit {
      position: absolute;
      font-size: 14px;
      top: 0;
      left: 0;
      line-height: 1;
      color: #FFF;
      text-shadow: 1px 1px 1px rgba(0, 0, 0, .5);
    }
  }

  .radar-chart-canvas {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/MapTools/WindSpeedRose/index.vue